import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { Annotation, EditorState } from '@codemirror/state';
import { EditorView, keymap, placeholder as cmPlaceholder } from '@codemirror/view';
import { useCallback, useEffect, useRef } from 'react';
import { useTypedText } from '../hooks/useTypedText';
import { editorExtensions } from '../lib/editorTheme';

/**
 * Marks a change this editor took in from elsewhere rather than one the user
 * typed. Sending an adopted change back out would make the editor answer every
 * echo with another write, and the round trip would never end.
 */
const adopted = Annotation.define<boolean>();

interface Props {
  /** Item id — changing it means a different note, so the document is replaced. */
  docKey: string;
  value: string;
  onChange: (value: string) => void;
  /** Hands the live view to the parent, so the toolbar and outline can drive it. */
  onReady?: (view: EditorView | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

/**
 * The note body. Markdown is kept as a plain string all the way down: it makes
 * last-write-wins sync trivial and export free, which rich text would not.
 */
export function MarkdownEditor({
  docKey,
  value,
  onChange,
  onReady,
  placeholder = 'Write…',
  autoFocus,
}: Props) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  // What the document should say, with text still in flight to the database
  // outranking the stale copy coming back from it.
  const [text, typed] = useTypedText(value, docKey);
  // Held in refs so swapping a handler never tears down the editor.
  const emit = useRef(onChange);
  emit.current = onChange;
  const ready = useRef(onReady);
  ready.current = onReady;
  const type = useRef(typed);
  type.current = typed;
  const wanted = useRef(text);
  wanted.current = text;

  /**
   * Puts the document back in step with what the database says, but never under
   * a live cursor: replacing the text someone is in the middle of writing is
   * the failure this whole file exists to prevent, and no incoming value is
   * worth it. A deferred change is picked up the moment the editor loses focus.
   */
  const reconcile = useCallback(() => {
    const instance = view.current;
    if (!instance || instance.hasFocus) return;
    const current = instance.state.doc.toString();
    if (current === wanted.current) return;
    instance.dispatch({
      changes: { from: 0, to: current.length, insert: wanted.current },
      annotations: adopted.of(true),
    });
  }, []);

  useEffect(() => {
    if (!host.current) return;

    const instance = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: text,
        extensions: [
          history(),
          keymap.of([...defaultKeymap, ...historyKeymap]),
          markdown({ base: markdownLanguage, codeLanguages: [] }),
          EditorView.lineWrapping,
          cmPlaceholder(placeholder),
          ...editorExtensions,
          EditorView.updateListener.of((u) => {
            if (!u.docChanged) return;
            if (u.transactions.some((t) => t.annotation(adopted))) return;
            const next = u.state.doc.toString();
            type.current(next);
            emit.current(next);
          }),
          // Whatever was held back while the cursor was here can land now.
          EditorView.domEventHandlers({
            blur: () => {
              window.setTimeout(reconcile, 0);
              return false;
            },
          }),
        ],
      }),
    });

    view.current = instance;
    ready.current?.(instance);
    if (autoFocus) instance.focus();
    return () => {
      ready.current?.(null);
      instance.destroy();
      view.current = null;
    };
    // Rebuilt only when the open item changes — not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey]);

  // Adopt changes that came from somewhere other than this editor (a sync pull,
  // an undo). `text` never carries the database's stale echo of the user's own
  // typing, which is the only thing that used to reach this line mid-word.
  useEffect(reconcile, [text, reconcile]);

  return <div className="editor__cm" ref={host} />;
}
